// Experiment editor panel: hand-edited (or AI-generated) JSON gets
// run against the sandbox, and signed-in users can save it (and
// optionally share publicly). The textarea is fully controlled —
// App owns the JSON string so the LibraryPanel can drop a saved
// experiment back into it.

import { useEffect, useState } from "react";

import { ApiError, api } from "../auth/client";
import { useAuth } from "../auth/AuthProvider";
import type { SandboxLike } from "../sim/types";

// Rotating placeholder examples for the Text-to-Experiment textbox.
// Cycles every 5 seconds while the textbox is empty so the user
// doesn't have to invent a starting prompt from scratch.
const PROMPT_EXAMPLES = [
  "drop water on hot lava and watch the steam rise",
  "ignite a wooden plank with a single spark in the bottom-left corner",
  "stamp a torch in the centre and check that smoke reaches the top in 200 ticks",
  "pour gasoline over a wood pile, then drop a fire from above",
  "freeze a column of water by pressing ice against it for 100 ticks",
  "fill the grid with sand, then carve a vertical line of empty so it falls",
];

export interface ExperimentPanelProps {
  sandbox: SandboxLike | null;
  /** Controlled value for the experiment JSON textarea. App owns this
   * so LibraryPanel can drop a saved experiment straight in. */
  json: string;
  onJsonChange: (next: string) => void;
  /** Open the auth modal when the user tries to save without being
   * signed in. Wired through App. */
  onOpenAuth: (mode: "signin" | "signup") => void;
}

export function ExperimentPanel({
  sandbox,
  json,
  onJsonChange,
  onOpenAuth,
}: ExperimentPanelProps) {
  const { user } = useAuth();
  const [status, setStatus] = useState<string | null>(null);
  // Public-share opt-in, off by default. Sticky across saves.
  const [makePublic, setMakePublic] = useState(false);
  // Text-to-Experiment state.
  const [prompt, setPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [exampleIdx, setExampleIdx] = useState(0);

  useEffect(() => {
    if (prompt.length > 0) return;
    const id = setInterval(() => {
      setExampleIdx((i) => (i + 1) % PROMPT_EXAMPLES.length);
    }, 5000);
    return () => clearInterval(id);
  }, [prompt.length]);

  const placeholder =
    PROMPT_EXAMPLES[exampleIdx] ?? PROMPT_EXAMPLES[0] ?? "describe your experiment...";

  const run = () => {
    if (!sandbox) return;
    const result = sandbox.runExperiment(json);
    setStatus(result === "" ? "experiment passed" : `failed: ${result}`);
  };

  const inferName = (raw: string): string => {
    try {
      const obj = JSON.parse(raw) as { name?: unknown };
      if (typeof obj.name === "string" && obj.name.length > 0) return obj.name;
    } catch {
      // fall through
    }
    return `experiment-${new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-")}`;
  };

  const downloadAsFile = (raw: string, filename: string) => {
    const blob = new Blob([raw], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename.endsWith(".json") ? filename : `${filename}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const save = async () => {
    setStatus(null);
    const name = inferName(json);
    // Always download a copy locally so the user has the file even if
    // their session expires mid-save or they're offline.
    downloadAsFile(json, name);
    if (!user) {
      onOpenAuth("signup");
      setStatus("downloaded locally — sign in to save to your library");
      return;
    }
    let payload: unknown;
    try {
      payload = JSON.parse(json);
    } catch {
      setStatus("invalid JSON; downloaded the raw text but skipped the library save");
      return;
    }
    try {
      await api.saveExperiment(name, payload, makePublic);
      setStatus(
        makePublic
          ? `saved "${name}" to your library (public)`
          : `saved "${name}" to your library`,
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onOpenAuth("signin");
        setStatus("session expired — sign in again");
      } else {
        setStatus(`save failed: ${err instanceof Error ? err.message : "unknown error"}`);
      }
    }
  };

  const generate = async () => {
    if (prompt.trim().length === 0) return;
    setAiError(null);
    if (!user) {
      onOpenAuth("signup");
      setAiError("Sign in to use the AI assistant.");
      return;
    }
    setGenerating(true);
    try {
      const { json: generated } = await api.textToExperiment(prompt.trim());
      // Pretty-print so the user can read what came back. The API
      // already validated it as JSON.
      try {
        onJsonChange(JSON.stringify(JSON.parse(generated), null, 2));
      } catch {
        onJsonChange(generated);
      }
      setStatus("generated; review and click Run experiment");
    } catch (err) {
      if (err instanceof ApiError && err.code === "ai_unavailable") {
        setAiError("AI is not configured on this deploy (ANTHROPIC_API_KEY missing).");
      } else if (err instanceof ApiError && err.status === 401) {
        onOpenAuth("signin");
        setAiError("Session expired — sign in again.");
      } else {
        setAiError(err instanceof Error ? err.message : "AI request failed");
      }
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="experiment-panel">
      <h2>Text-to-Experiment</h2>
      <textarea
        className="formula-input"
        rows={3}
        value={prompt}
        placeholder={placeholder}
        onChange={(e) => {
          setPrompt(e.target.value);
          setAiError(null);
        }}
        spellCheck={false}
      />
      <button
        type="button"
        className="formula-run"
        onClick={generate}
        disabled={generating || prompt.trim().length === 0}
      >
        {generating ? "Generating..." : "Generate experiment"}
      </button>
      {aiError && <div className="formula-status">{aiError}</div>}

      <div className="experiment-json-header">
        <h2>Experiment JSON</h2>
        <button
          type="button"
          className="formula-secondary"
          onClick={() => {
            if (!sandbox) return;
            const name = `snapshot-${new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-")}`;
            const out = sandbox.snapshotExperiment(name);
            onJsonChange(out);
            setStatus("snapshot of current sandbox written to JSON");
          }}
          disabled={!sandbox}
          title="Capture the current sandbox layout as a runnable experiment JSON"
        >
          Snapshot current experiment
        </button>
      </div>
      <textarea
        className="formula-input experiment-input"
        rows={12}
        value={json}
        onChange={(e) => {
          onJsonChange(e.target.value);
          setStatus(null);
        }}
        spellCheck={false}
      />
      <div className="panel-actions">
        <button
          type="button"
          className="formula-run"
          onClick={run}
          disabled={!sandbox}
        >
          Run experiment
        </button>
        <button
          type="button"
          className="formula-secondary"
          onClick={save}
          title={user ? "Save to your library" : "Download + sign in to save to your library"}
        >
          Save experiment
        </button>
        <label className="public-toggle" title="Show this experiment in Public Experiments after saving">
          <input
            type="checkbox"
            checked={makePublic}
            onChange={(e) => setMakePublic(e.target.checked)}
          />
          <span>Public</span>
        </label>
      </div>
      {status && <div className="formula-status">{status}</div>}
    </div>
  );
}
